// lib/store/dialogStore.ts
import type { JSX } from "react";
import { create } from "zustand";

export type DialogType = "CREATE" | "UPDATE" | "DELETE" | "CONFIRM" | "BATCH" | "INPUT";

export interface DialogState {
  type: DialogType | null;
  open: boolean;

  input: string;
  compareValue: string;
  title: string;
  loading: boolean;
  loadingMessage: string;
  description: string | JSX.Element;
  saveText: string;
  cancelText: string;
  showSecond: boolean;
  inputType?: string;
  errorMsg: string;
  onConfirm?: (val?: string, func?: any) => void;
  onCancel?: () => void;

  openDialog: (opts: {
    type: DialogType;
    title: string;
    description?: string | JSX.Element;
    compareValue?: string;
    saveText?: string;
    cancelText?: string;
    showSecond?: boolean;
    loadingMessage?: string;
    inputType?: string;
    onConfirm?: (val?: string, func?: any) => void;
    onCancel?: () => void;
  }) => void;
  closeDialog: () => void;
  setInput: (input: string) => void;
  setLoading: (loading: boolean) => void;
  setLoadingMessage: (message: string) => void;
  setErrorMsg: (errorMsg: string) => void;
}

export const useDialogStore = create<DialogState>((set) => ({
  // 초기 상태
  type: null,
  open: false,
  input: "",
  title: "",
  compareValue: "", // ★ 비교값 초기화
  loading: false,
  loadingMessage: "",
  description: "",
  saveText: "확인",
  cancelText: "취소",
  showSecond: false,
  inputType: "text",
  errorMsg: "",
  onConfirm: undefined,
  onCancel: undefined,

  // 다이얼로그 열기
  openDialog: ({
    type,
    title,
    description = "",
    compareValue = "", // ★ 비교값 설정
    saveText = type === "DELETE" ? "삭제" : type === "UPDATE" ? "변경" : type === "INPUT" ? "확인" : "생성",
    cancelText = "취소",
    showSecond = false,
    loadingMessage = "",
    inputType = "text",
    onConfirm,
    onCancel,
  }) =>
    set({
      type,
      open: true,
      input: "", // ★ 입력은 항상 빈칸으로
      title,
      compareValue, // ★
      loading: false,
      loadingMessage,
      description,
      saveText,
      cancelText,
      showSecond,
      inputType,
      errorMsg: "",
      onConfirm,
      onCancel,
    }),

  // 다이얼로그 닫기
  closeDialog: () =>
    set({
      type: null,
      open: false,
      input: "",
      title: "",
      compareValue: "", // ★ reset
      loading: false,
      loadingMessage: "",
      description: "",
      saveText: "확인",
      cancelText: "취소",
      showSecond: false,
      inputType: "text",
      errorMsg: "",
      onConfirm: undefined,
      onCancel: undefined,
    }),

  setInput: (input) => set({ input }),
  setLoading: (loading) => set({ loading }),
  setLoadingMessage: (loadingMessage) => set({ loadingMessage }),
  setErrorMsg: (errorMsg) => set({ errorMsg }),
}));

export function useDialog() {
  const openDialog = useDialogStore((s) => s.openDialog);
  const closeDialog = useDialogStore((s) => s.closeDialog);
  const setLoadingMessage = useDialogStore((s) => s.setLoadingMessage);
  const setErrorMsg = useDialogStore((s) => s.setErrorMsg);
  return { openDialog, closeDialog, setLoadingMessage, setErrorMsg };
}
